import { Component, OnInit } from '@angular/core';
import { AlertTypes } from "../../shared/alert/shared/alert-types";
import { AlertService } from "../../shared/alert/shared/alert.service";
import { CompanyData } from "../../company/shared/interfaces/company-data.interface";
import { CreateUser } from "../shared/interfaces/create-user.interface";
import { UserService } from "../shared/services/user.service";
import { CompanyService } from "../../company/shared/services/company.service";
import { SelectInput } from "../../shared/dropdown/shared/select-input.class";
import { ActivatedRoute, Router } from "@angular/router";
import { RoleData } from "../shared/interfaces/role-data.interface";
import { UserDataResponse } from "../shared/interfaces/login-data-response.interface";
import { EditUser } from "../shared/interfaces/edit-user.interface";
import { UserDataTypes } from "../shared/enums/user-data-types.enum";
import { RoleTypes } from "../shared/enums/role.enum";
import { UserProjects } from "../shared/interfaces/user-projects.interface";
import { ModalService } from "../../shared/modal/shared/modal.service";
import { MatDialog } from "@angular/material/dialog";
import { UserProjectAddModalComponent } from "../user-project-add-modal/user-project-add-modal.component";
import { UserGroupErrorTypeService } from '../shared/services/user-group-error-type.service';
import { UserGroupErrorType } from '../shared/interfaces/user-group-error-type';
import { group } from '@angular/animations';
import { TwoFactorRequirementMode } from '../shared/interfaces/two-factor-policy.interface';


@Component({
  selector: 'avs-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.css']
})
export class UserSettingsComponent implements OnInit {
  public isCompanyDropdownOpen: boolean = false;
  public isRoleDropdownOpen: boolean = false;
  public isCardChanged: boolean = false;
  public suspend2FA: boolean = false;  
  public companySelectInput: SelectInput[] = [];
  public selectedCompany?: SelectInput;
  public roleSelectInput: SelectInput[] = [];
  public selectedRole: RoleData | undefined;
  public userId: number | undefined;
  public assignedProjects: UserProjects[] | undefined;
  public userData: CreateUser = {
    title: "", firstName: "", lastName: "",
    email: "", company: 0, isAuthorizedForDownloads: false, isAuthorizedForAlarms: false, isAlarmsActivated: true, isAcousticAlarmsActivated: true, isTwoFactorRequired: false, role: 7
  } as CreateUser
  // Edit mode only: an admin may set a new password for the user (optional). Holds the confirmed
  // value from avs-password-confirm-input, or '' while the two inputs do not match.
  // On creation no password is assigned any more; the user sets it via the emailed link.
  public newPassword: string = '';
  public isPasswordSectionOpen: boolean = false;
  // Set after a failed save attempt; the template then outlines the missing required fields in red.
  public showValidationErrors: boolean = false;
  public hasUserTwoFactorEnabled : boolean = false;
  public userGroupErrorType: UserGroupErrorType[] = [];
  // Aktuelle Datenschutz-Zustimmung des bearbeiteten Users (nur lesbar) und ob der Admin
  // sie zuruecksetzen will. Erteilen kann die Zustimmung nur der Nutzer selbst.
  public originalAcceptTerms: boolean = false;
  public resetAcceptTerms: boolean = false;


  private roleData: RoleData[] = [];
  private twoFactorRequirementMode?: TwoFactorRequirementMode;

  private editUserData: EditUser | undefined;
  private companies: CompanyData[] = [];


  protected readonly UserDataTypes = UserDataTypes;

  constructor(private alertService: AlertService,
    private userService: UserService,
    private companyService: CompanyService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    public dialog: MatDialog, private userGroupErrorTypeService: UserGroupErrorTypeService) {
  }

  public ngOnInit(): void {
    this.getData();

  }

  public onBackIconClicked(): void {
    this.router.navigate(['user/']);
  }

  public onCompanyDropdownValueChange(newValue: SelectInput): void {
    this.selectedCompany = newValue;
    if (newValue.id) {
      this.userData.company = +newValue.id;
    }
    this.isCompanyDropdownOpen = false;
    this.isCardChanged = true;
  }

  public onRoleDropdownValueChange(newValue: SelectInput): void {
    this.selectedRole = this.roleData.find((i => i.name === newValue.name && i.id === newValue.id))
    this.applyTwoFactorRequirementPolicy();
    this.isRoleDropdownOpen = false;
    this.isCardChanged = true;
  }

  public get isTwoFactorRequirementLocked(): boolean {
    return this.twoFactorRequirementMode === 'AdminsRequired' && this.selectedRole?.id === RoleTypes.admin;
  }

  public onTwoFactorRequiredChange(isRequired: boolean): void {
    if (this.isTwoFactorRequirementLocked) {
      this.userData.isTwoFactorRequired = true;
      return;
    }

    this.userData.isTwoFactorRequired = isRequired;
    this.isCardChanged = true;
  }

    public onTwoFactorSuspend(suspend: boolean): void {
    this.suspend2FA = suspend;
    this.isCardChanged = true;
  }


  public onResetAcceptTermsChange(reset: boolean): void {
    this.resetAcceptTerms = reset;
    this.isCardChanged = true;
  }

  public onCompanyDataValueChange(newValue: any, type: UserDataTypes): void { /* das ist eine Frechheit was die Agentur hier damals gemacht hat */
    switch (type) {
      case UserDataTypes.title:
        this.userData.title = newValue;
        break;
      case UserDataTypes.firstName:
        this.userData.firstName = newValue;
        break;
      case UserDataTypes.lastName:
        this.userData.lastName = newValue;
        break;
      case UserDataTypes.email:
        this.userData.email = newValue;
        break;
    }
    this.isCardChanged = true;
  }

  public onNewPasswordChange(newValue: string): void {
    this.newPassword = newValue;
    this.isCardChanged = true;
  }

  public onPasswordSectionToggleClicked(): void {
    this.isPasswordSectionOpen = !this.isPasswordSectionOpen;
    // Collapsing discards a typed password so nothing hidden is sent on save.
    if (!this.isPasswordSectionOpen)
      this.newPassword = '';
  }

  public onCompanyDropdownClicked(): void {
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
  }

  public onRoleDropdownClicked(): void {
    this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
  }

  public onSaveCardBtnClicked(): void {
    if (this.userId) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  public onAddProjectBtnClicked(): void {
    this.addProjects();
  }

  public onDeleteBtnClicked(projectId: number): void {
    this.deleteProject(projectId);
  }

  private deleteProject(projectId: number): void {
    this.modalService.openDialog('Das Projekt vom Benutzer entfernen?', 'Nein', 'Ja')
      .subscribe(() => {
        if (this.userId) {
          this.userService.editUserProject(this.userId, [], [projectId])
            .subscribe(() => {
              this.getProjectsAssignedToUser();
            })
        }
      });
  }

  private addProjects(): void {
    const dialogRef = this.dialog.open(UserProjectAddModalComponent, { data: this.assignedProjects });
    dialogRef.afterClosed().subscribe(result => {
      if (result && this.userId) {
        this.userService.editUserProject(this.userId, [result.id], [])
          .subscribe(projects => {
            this.getProjectsAssignedToUser();
          })
      }
    });
  }

  private updateUser(): void {

    /* what the hell ? */
    this.setUpdateUserData();
    if (!this.validUserData()) {
      return;
    }

    if (this.editUserData) {
      this.userService.editUserData(this.userId, this.editUserData).subscribe(updatedUser => {
        /*         this.userService.assignRoleToUser(updatedUser.id, this.assignedRole).subscribe(() => {
                  this.alertService.alert('Die Änderungen wurden erfolgreich gespeichert', AlertTypes.success);
                  this.isCardChanged = false;
                  this.router.navigate(['user/']);
                }) */
/*         console.log('Die Änderungen wurden erfolgreich gespeichert', updatedUser);
 */        this.alertService.alert('Die Änderungen wurden erfolgreich gespeichert', AlertTypes.success);
        this.isCardChanged = false;
        this.router.navigate(['user/']);
      })
    }
  }

  private setUpdateUserData() {

    this.applyTwoFactorRequirementPolicy();

    this.editUserData = {
      firstName: this.userData.firstName,
      lastName: this.userData.lastName,
      title: this.userData.title,
      email: this.userData.email,
      newPassword: this.newPassword || undefined,
      company: this.userData.company,
      isAuthorizedForAlarms: this.userData.isAuthorizedForAlarms,
      isAuthorizedForDownloads: this.userData.isAuthorizedForDownloads,
      isAlarmsActivated: this.userData.isAlarmsActivated,
      isAcousticAlarmsActivated: this.userData.isAcousticAlarmsActivated,
      isTwoFactorRequired: this.userData.isTwoFactorRequired,
      // Nur Zuruecksetzen (false) senden; niemals true (Zustimmung erteilt nur der Nutzer selbst).
      // Ohne Reset: acceptTerms weglassen -> Backend laesst den Wert unveraendert.
      acceptTerms: this.resetAcceptTerms ? false : undefined,
      // Nur ggf. einmalig aussetzen.
      suspendTwoFactor: this.suspend2FA ? true: undefined,
      role: this.selectedRole ? this.selectedRole.id : 7
    } as EditUser
  }
  /* old password leerer string -> newPassword */
  private getUserId(): void {
    this.activatedRoute.params.subscribe(params => {
      this.userId = params['id'];
    });
  }

  private getUserData(): void {
    if (this.userId) {
      this.userService.getUserById(this.userId).subscribe(userData => {
        this.setCurrentUserData(userData);
      });
    }
  }

  private setCurrentUserData(userData: UserDataResponse): void {
    this.selectedCompany = this.companySelectInput.find(i => i.id === userData.company);

    this.userData.firstName = userData.firstName;
    this.userData.lastName = userData.lastName;
    this.userData.title = userData.title;
    this.userData.email = userData.email;
    this.userData.isAcousticAlarmsActivated = userData.isAcousticAlarmsActivated ?? true;
    this.userData.isAlarmsActivated = userData.isAlarmsActivated ?? true;
    this.userData.isAuthorizedForAlarms = userData.isAuthorizedForAlarms ?? false;
    this.userData.isAuthorizedForDownloads = userData.isAuthorizedForDownloads ?? false;
    this.selectedRole = this.roleData.find((i => i.id === userData.role));
    this.userData.isTwoFactorRequired = userData.isTwoFactorRequired ?? false;
    this.hasUserTwoFactorEnabled = userData.isTwoFactorEnabled ?? false;
    this.suspend2FA = false;
    this.originalAcceptTerms = userData.acceptTerms ?? false;
    this.resetAcceptTerms = false;
    this.applyTwoFactorRequirementPolicy();
    if (this.selectedCompany)
      this.onCompanyDropdownValueChange({ name: this.selectedCompany.name, id: this.selectedCompany.id })
  }

  private getCompanyData(): void {
    this.companyService.getCompanys().subscribe(companyData => {
      this.companies = companyData;
      this.setCompanySelectInput(companyData);
      this.getUserId();
      if (this.userId) {
        this.getUserData();
        this.getProjectsAssignedToUser();
      }
    });
  }

  private getAllRoles() {
    let roleData = [{ name: 'Admin', id: 1 },
    { name: 'User', id: 2 },
    { name: 'Extern', id: 3 },
    { name: 'Monteur', id: 4 },
    { name: 'AdminReadOnly', id: 5 },
    { name: 'UserReadOnly', id: 7 },
    { name: 'SystemUser', id: 8 },
    { name: 'User+Reports', id: 9 },
    ]
    this.setRoleSelectInput(roleData);

  }

  private getProjectsAssignedToUser(): void {
    this.userService.getProjectsAssignedToUser(this.userId).subscribe(assignedProjects => {
      this.assignedProjects = assignedProjects;
      this.getUserGroupErrorType(this.userId!);
    })
  }

  private setCompanySelectInput(companyData: CompanyData[]): void {
    this.companySelectInput = [];
    for (let company of companyData) {
      let selectInput = {
        name: company.companyName,
        id: company.id
      } as SelectInput;
      this.companySelectInput.push(selectInput);
    }
  }

  private setRoleSelectInput(roleData: RoleData[]): void {
    this.roleData = roleData;
    for (let role of this.roleData) {
      let selectInput = {
        name: role.name,
        id: role.id
      } as SelectInput;
      this.roleSelectInput.push(selectInput);
    }

    // New user: preselect the least privileged role so the dropdown never starts empty.
    if (!this.activatedRoute.snapshot.params['id'] && !this.selectedRole) {
      this.selectedRole = this.roleData.find(role => role.id === RoleTypes.UserReadOnly);
      this.applyTwoFactorRequirementPolicy();
    }
  }

  private getUserGroupErrorType(userId: number): void {
    this.userGroupErrorTypeService.getUserGroupErrorTypeByUserId(userId).subscribe((x) => {
      this.userGroupErrorType = [];
      this.userGroupErrorType = x;
      if (this.userGroupErrorType.length > 0) {
        this.userGroupErrorType.forEach((x) => {
          this.assignedProjects?.forEach((y) => {
            y.groups.forEach((z) => {
              if (x.groupId === z.id) {
                /* jetzt prüfen welcher Fehlertyp und diesen dann auf true setzen :D */
                if (x.type === 1) { z.userGroupErrorTypeChannelError = true }
                if (x.type === 2) { z.userGroupErrorTypePixelError = true }
                if (x.type === 3) { z.userGroupErrorTypeVoltageError = true }
                if (x.type === 4) { z.userGroupErrorTypeFuelWarning = true }
                if (x.type === 5) { z.userGroupErrorTypeSystemStatusFuelcell = true }
                if (x.type === 6) { z.userGroupErrorTypeSTMInput = true }
                if (x.type === 7) { z.userGroupErrorTypeLampError = true }
              }
            })
          })
        })
      }
    })
  }

  public changeUserGroupErrorTypeChannel(groupId: number, group?: any): void {
    if (group.userGroupErrorTypeChannelError === true) {

      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 1) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe((x) => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeChannelError = false;
          })
        }
      })
    }
    if (group.userGroupErrorTypeChannelError === false || group.userGroupErrorTypeChannelError === undefined) {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 1,
        groupId: groupId
      }
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe((x) => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeChannelError = true;
      })
    }
  }

  public changeUserGroupErrorTypePixel(groupId: number, group: any): void {
    if (group.userGroupErrorTypePixelError) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 2) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypePixelError = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 2,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypePixelError = true;
      });
    }
  }

  public changeUserGroupErrorTypeVoltage(groupId: number, group: any): void {
    if (group.userGroupErrorTypeVoltageError) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 3) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeVoltageError = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 3,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeVoltageError = true;
      });
    }
  }

  public changeUserGroupErrorTypeFuelWarning(groupId: number, group: any): void {
    if (group.userGroupErrorTypeFuelWarning) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 4) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeFuelWarning = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 4,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeFuelWarning = true;
      });
    }
  }

  public changeUserGroupErrorTypeSystemStatusFuelcell(groupId: number, group: any): void {
    if (group.userGroupErrorTypeSystemStatusFuelcell) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 5) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeSystemStatusFuelcell = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 5,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeSystemStatusFuelcell = true;
      });
    }
  }

  public changeUserGroupErrorTypeSTMInput(groupId: number, group: any): void {
    if (group.userGroupErrorTypeSTMInput) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 6) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeSTMInput = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 6,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);

        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeSTMInput = true;
      });
    }
  }

  public changeUserGroupErrorTypeLampError(groupId: number, group: any): void {
    if (group.userGroupErrorTypeLampError) {
      this.userGroupErrorType.forEach((x) => {
        if (x.groupId === groupId && x.type === 7) {
          this.userGroupErrorTypeService.deleteUserGroupErrorType(x.id!).subscribe(() => {
            this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich gelöscht', AlertTypes.success);
            this.getUserGroupErrorType(this.userId!);
            group.userGroupErrorTypeLampError = false;
          });
        }
      });
    } else {
      let userGroupErrorType: UserGroupErrorType = {
        userId: this.userId!,
        type: 7,
        groupId: groupId
      };
      this.userGroupErrorTypeService.createUserGroupErrorType(userGroupErrorType).subscribe(() => {
        this.getUserGroupErrorType(this.userId!);
        this.alertService.alert('Die Fehlerüberwachung wurde erfolgreich hinzugefügt', AlertTypes.success);
        group.userGroupErrorTypeLampError = true;
      });
    }
  }

  private createUser(): void {
    if (!this.validUserData()) {
      return;
    }
    this.userData.role = this.selectedRole ? this.selectedRole.id : 7;
    this.applyTwoFactorRequirementPolicy();


    this.userService.createUser(this.userData).subscribe(newUser => {
      this.alertService.alert(
        'Der Benutzer wurde erstellt. Eine E-Mail mit dem Link zum Setzen des Passworts wurde versendet.',
        AlertTypes.success
      );
      this.isCardChanged = false;
      this.userId = newUser.id;

    })
  }

  private validUserData(): boolean {
    const isComplete = !!this.userData.firstName && !!this.userData.lastName && !!this.userData.email && !!this.userData.company;
    this.showValidationErrors = !isComplete;
    if (!isComplete)
      this.alertService.alert('Daten nicht vollständig', AlertTypes.error);
    return isComplete;
  }

  /** CSS class for a required input that is still empty after a failed save attempt. */
  public missingClass(value: string | number | undefined): string {
    return this.showValidationErrors && !value ? 'border-danger' : '';
  }

  private getData(): void {
    this.getTwoFactorPolicy();
    this.getCompanyData();
    this.getAllRoles()

  }

  private getTwoFactorPolicy(): void {
    this.userService.getTwoFactorPolicy().subscribe({
      next: policy => {
        this.twoFactorRequirementMode = policy.requirementMode;
        this.applyTwoFactorRequirementPolicy();
      },
      error: () => {
        this.twoFactorRequirementMode = 'AdminsRequired';
        this.applyTwoFactorRequirementPolicy();
      }
    });
  }

  private applyTwoFactorRequirementPolicy(): void {
    if (this.isTwoFactorRequirementLocked) {
      this.userData.isTwoFactorRequired = true;
    }
  }
}
